/**
 * Cart Drawer Controller
 * Handles opening/closing drawer and rendering cart items
 */

function sacksPerLineUnit(item) {
  const p = item.properties;
  if (!p || typeof p !== 'object' || Array.isArray(p)) return 1;
  const raw =
    p._tier_units ??
    p._sack_count ??
    p['_tier_units'] ??
    p['_sack_count'];
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function totalSackCount(items) {
  return items.reduce((sum, item) => {
    const lineQuantity = item.quantity || 1;
    return sum + (lineQuantity * sacksPerLineUnit(item));
  }, 0);
}

const CartDrawer = {
  overlay: null,
  drawer: null,
  focusableElements: null,
  firstFocusableElement: null,
  lastFocusableElement: null,
  
  /**
   * Initialize cart drawer
   */
  init() {
    this.overlay = document.getElementById('cart-overlay');
    this.drawer = document.getElementById('cart-drawer');
    
    if (!this.drawer) {
      console.warn('Cart drawer element not found');
      return;
    }
    
    // Toggle buttons
    const cartToggle = document.getElementById('cart-toggle');
    const cartClose = document.getElementById('cart-close');
    
    if (cartToggle) {
      cartToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }
    
    if (cartClose) {
      cartClose.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    }
    
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    
    // Checkout button
    const checkoutBtn = document.getElementById('cart-checkout');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => this.checkout());
    }
    
    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      this.render();
    });
    
    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
        this.close();
      }
    });
    
    // Keyboard navigation - focus trap
    if (this.drawer) {
      this.drawer.addEventListener('keydown', (e) => this.handleKeyDown(e));
    }
    
    // Initial render
    this.render();
  },
  
  /**
   * Update focusable elements list
   */
  updateFocusableElements() {
    if (!this.drawer) return;
    
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    this.focusableElements = Array.from(this.drawer.querySelectorAll(focusableSelectors));
    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  },
  
  /**
   * Handle keyboard navigation in drawer
   */
  handleKeyDown(e) {
    if (!this.drawer?.classList.contains('is-open')) return;
    
    // Tab key - trap focus
    if (e.key === 'Tab') {
      this.updateFocusableElements();
      
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === this.firstFocusableElement) {
          e.preventDefault();
          this.lastFocusableElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === this.lastFocusableElement) {
          e.preventDefault();
          this.firstFocusableElement?.focus();
        }
      }
    }
  },

  /**
   * Read active campaign config from cart drawer data attributes.
   */
  getCampaignConfig() {
    const source = this.drawer || document.getElementById('cart-drawer');
    const minQty = parseInt(source?.dataset.campaignMinQty || '2', 10);
    return {
      active: source?.dataset.campaignActive === 'true',
      code: source?.dataset.campaignCode || 'VED15',
      minQty: Number.isFinite(minQty) && minQty > 0 ? minQty : 2,
    };
  },
  
  /**
   * Open cart drawer
   */
  open() {
    if (!this.drawer) return;
    
    this.render();
    this.overlay?.classList.add('is-open');
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.overlay?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Update cart toggle aria-expanded
    const cartToggle = document.getElementById('cart-toggle');
    if (cartToggle) {
      cartToggle.setAttribute('aria-expanded', 'true');
    }
    
    // Update focusable elements and focus first one
    this.updateFocusableElements();
    this.firstFocusableElement?.focus();
  },
  
  /**
   * Close cart drawer
   */
  close() {
    if (!this.drawer) return;
    
    this.overlay?.classList.remove('is-open');
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.overlay?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    // Update cart toggle aria-expanded
    const cartToggle = document.getElementById('cart-toggle');
    if (cartToggle) {
      cartToggle.setAttribute('aria-expanded', 'false');
      // Return focus to toggle button for accessibility
      cartToggle.focus();
    }
  },
  
  /**
   * Render cart items
   */
  async render() {
    if (!this.drawer) return;
    
    const itemsContainer = document.getElementById('cart-items');
    const emptyState = document.getElementById('cart-empty');
    const footer = document.getElementById('cart-footer');
    
    if (!itemsContainer || !emptyState || !footer) {
      console.warn('Cart drawer elements not found');
      return;
    }
    
    try {
      const items = await CartStore.getItems();
      const totals = await CartStore.getTotals();
      
      if (items.length === 0) {
        itemsContainer.style.display = 'none';
        emptyState.style.display = 'flex';
        footer.style.display = 'none';
        return;
      }
      
      itemsContainer.style.display = 'flex';
      emptyState.style.display = 'none';
      footer.style.display = 'block';
      
      // Render items
      itemsContainer.innerHTML = items.map(item => {
        const imageUrl = item.image || item.featured_image?.url || '/assets/no-image.png';
        const imageAlt = item.product_title || item.title || 'Produktbild';
        const title = item.product_title || item.title || 'Produkt';
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
        const price = (item.final_price || item.price || 0) / 100; // Convert from cents
        const lineQuantity = item.quantity || 1;
        const packSacks = sacksPerLineUnit(item);
        const displayQuantity = lineQuantity * packSacks;
        const lineItemKey = item.key || item.id;
        
        return `
          <div class="cart-item" data-line-item-key="${lineItemKey}" data-variant-id="${item.variant_id}">
            <img 
              src="${imageUrl}" 
              alt="${imageAlt}" 
              class="cart-item__image"
              loading="lazy"
            >
            <div class="cart-item__info">
              <h4 class="cart-item__title">${this.escapeHtml(title)}</h4>
              ${variantTitle ? `<p class="cart-item__variant">${this.escapeHtml(variantTitle)}</p>` : ''}
              <p class="cart-item__price">${this.formatPrice(price)}</p>
            </div>
            <div class="cart-item__actions">
              <button 
                class="cart-item__remove" 
                data-action="remove" 
                data-line-item-key="${lineItemKey}"
                aria-label="Ta bort ${this.escapeHtml(title)}"
                type="button"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 6h18"/>
                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                </svg>
              </button>
              <div class="cart-item__quantity">
                <button 
                  class="cart-item__quantity-btn" 
                  data-action="decrease" 
                  data-line-item-key="${lineItemKey}"
                  aria-label="Minska antal"
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14"/>
                  </svg>
                </button>
                <span class="cart-item__quantity-value">${displayQuantity}</span>
                <button 
                  class="cart-item__quantity-btn" 
                  data-action="increase" 
                  data-line-item-key="${lineItemKey}"
                  aria-label="Öka antal"
                  type="button"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 5v14"/>
                    <path d="M5 12h14"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        `;
      }).join('');
      
      // Update totals
      const subtotalEl = document.getElementById('cart-subtotal');
      const shippingEl = document.getElementById('cart-shipping');
      const totalEl = document.getElementById('cart-total');
      
      if (subtotalEl) {
        subtotalEl.textContent = this.formatPrice(totals.subtotal);
      }
      
      // Get shipping cost from delivery info
      let shippingCost = 0;
      let shippingText = 'Beräknas i kassan';
      
      console.log('[Cart Drawer] Checking delivery info...');
      
      if (typeof getDeliveryInfo !== 'undefined') {
        const deliveryInfo = getDeliveryInfo();
        console.log('[Cart Drawer] Delivery info:', deliveryInfo);
        
        if (deliveryInfo && deliveryInfo.valid) {
          console.log('[Cart Drawer] Delivery info is valid');
          console.log('[Cart Drawer] Price value:', deliveryInfo.price);
          console.log('[Cart Drawer] Price type:', typeof deliveryInfo.price);
          console.log('[Cart Drawer] Price !== undefined:', deliveryInfo.price !== undefined);
          console.log('[Cart Drawer] Price !== null:', deliveryInfo.price !== null);
          
          // deliveryInfo.price can be 0 (fri frakt), so check for !== undefined instead of truthy
          if (deliveryInfo.price !== undefined && deliveryInfo.price !== null) {
            shippingCost = Number(deliveryInfo.price); // Ensure it's a number
            console.log('[Cart Drawer] Shipping cost set to:', shippingCost);
            
            // Show "Fri frakt" when cost is 0, otherwise show formatted price
            if (shippingCost === 0) {
              shippingText = 'Fri frakt';
              console.log('[Cart Drawer] Setting shipping text to "Fri frakt"');
            } else {
              shippingText = this.formatPrice(shippingCost);
              console.log('[Cart Drawer] Setting shipping text to:', shippingText);
            }
          } else {
            console.log('[Cart Drawer] Price is undefined or null, keeping default');
          }
        } else {
          console.log('[Cart Drawer] No delivery info or not valid');
        }
      } else {
        console.log('[Cart Drawer] getDeliveryInfo function not available');
      }
      
      console.log('[Cart Drawer] Final shipping text:', shippingText);
      
      if (shippingEl) {
        shippingEl.textContent = shippingText;
      }
      
      // Calculate total with shipping
      const totalWithShipping = totals.totalPrice + shippingCost;
      if (totalEl) {
        totalEl.textContent = this.formatPrice(totalWithShipping);
      }
      
      // Add event listeners to action buttons
      itemsContainer.querySelectorAll('[data-action]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const action = e.currentTarget.dataset.action;
          const lineItemKey = e.currentTarget.dataset.lineItemKey;
          
          if (!lineItemKey) {
            console.error('Line item key not found');
            return;
          }
          
          try {
            if (action === 'remove') {
              await CartStore.removeItem(lineItemKey);
            } else if (action === 'decrease') {
              const item = items.find(i => (i.key || i.id) === lineItemKey);
              if (item && item.quantity > 1) {
                await CartStore.updateQuantity(lineItemKey, item.quantity - 1);
              } else if (item && item.quantity === 1) {
                await CartStore.removeItem(lineItemKey);
              }
            } else if (action === 'increase') {
              const item = items.find(i => (i.key || i.id) === lineItemKey);
              if (item) {
                await CartStore.updateQuantity(lineItemKey, item.quantity + 1);
              }
            }
            
            // Re-render after update
            await this.render();
          } catch (error) {
            console.error('Error updating cart:', error);
            alert('Ett fel uppstod när kundvagnen uppdaterades. Försök igen.');
          }
        });
      });
    } catch (error) {
      console.error('Error rendering cart drawer:', error);
      itemsContainer.innerHTML = '<p>Ett fel uppstod när kundvagnen laddades.</p>';
    }
  },
  
  /**
   * Checkout - redirect to Shopify checkout
   * Sends postal code and shipping cost as cart attributes
   */
  async checkout() {
    const checkoutBtn = document.getElementById('cart-checkout');
    const originalHTML = checkoutBtn?.innerHTML;
    
    try {
      const items = await CartStore.getItems();
      if (items.length === 0) {
        this.close();
        return;
      }
      
      if (checkoutBtn) {
        checkoutBtn.disabled = true;
        checkoutBtn.innerHTML = 'Skapar order...';
      }
      
      // Get delivery info and send as cart attributes
      if (typeof getDeliveryInfo !== 'undefined') {
        const deliveryInfo = getDeliveryInfo();
        if (deliveryInfo && deliveryInfo.valid) {
          try {
            // Update cart with delivery info as attributes
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: {
                  'Postnummer': deliveryInfo.postalCode,
                  'Fraktzon': deliveryInfo.zone,
                  'Fraktkostnad': deliveryInfo.price.toString()
                }
              })
            });
          } catch (error) {
            console.warn('Could not update cart attributes:', error);
            // Continue to checkout anyway
          }
        }
      }
      
      const campaign = this.getCampaignConfig();
      const checkoutUrl =
        campaign.active && campaign.code && totalSackCount(items) >= campaign.minQty
          ? `/discount/${encodeURIComponent(campaign.code)}?redirect=${encodeURIComponent('/checkout')}`
          : '/checkout';
      
      // Redirect to Shopify checkout, with discount code when the campaign qualifies.
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Checkout error:', error);
      if (checkoutBtn) {
        checkoutBtn.disabled = false;
        checkoutBtn.innerHTML = originalHTML || 'Till kassan';
      }
      alert('Ett fel uppstod när kassan öppnades. Försök igen.');
    }
  },
  
  /**
   * Format price for display
   * @param {number} price - Price in currency units
   * @returns {string} Formatted price string
   */
  formatPrice(price) {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  },
  
  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  },
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    CartDrawer.init();
  });
} else {
  CartDrawer.init();
}

// Export for use in other scripts
window.CartDrawer = CartDrawer;

